import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";
import { SystemEntity } from "@erp/shared-types";

export const ENTITY_RESOLVER_RESERVED_SOCKET_IDS = [
  "matched_entity_type",
  "matched_entity_id",
  "match_score",
  "matching_reasoning",
] as const;

const entityOutputSchema = z.object({
  entityType: z.nativeEnum(SystemEntity).describe("The SystemEntity type"),
  socketId: z
    .string()
    .regex(
      /^[a-z][a-z0-9_]*$/,
      "Socket id must be lowercase alphanumeric with underscores",
    )
    .refine(
      (id) =>
        !(ENTITY_RESOLVER_RESERVED_SOCKET_IDS as readonly string[]).includes(
          id,
        ),
      {
        message: `Socket id cannot be one of the reserved ids: ${ENTITY_RESOLVER_RESERVED_SOCKET_IDS.join(", ")}`,
      },
    )
    .describe("Output socket id for this entity type"),
  label: z
    .string()
    .optional()
    .describe("Optional display label for the socket"),
});

export const entityResolverStep = defineStepConfig({
  type: "entity_resolver",
  name: "Entity Resolver",
  description:
    "Resolves input to one or more system entities by type, with confidence-ordered results and switch-case style routing",
  category: "AI & Agents",

  inputSchema: z.object({}).passthrough(),

  outputSchema: z.record(z.string(), z.any()),

  configSchema: z.object({
    dataInputs: z
      .array(
        z.object({
          key: z
            .string()
            .regex(
              /^[a-z][a-z0-9_]*$/,
              "Key must be lowercase alphanumeric with underscores",
            )
            .describe("Input socket id"),
          label: z.string().optional().describe("Display label for the socket"),
        }),
      )
      .default([{ key: "data", label: "Data" }])
      .superRefine((inputs, ctx) => {
        const seen = new Set<string>();
        inputs.forEach((input, index) => {
          if (seen.has(input.key)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: `Duplicate dataInputs[].key: ${input.key}`,
              path: [index, "key"],
            });
            return;
          }
          seen.add(input.key);
        });
      })
      .describe(
        "Dynamic data input sockets. Each becomes an input the user can wire.",
      ),
    topK: z
      .number()
      .int()
      .min(1)
      .max(25)
      .default(1)
      .describe(
        "Number of results per entity type. When > 1, output sockets emit arrays ordered by confidence descending.",
      ),
    minScore: z
      .number()
      .min(0)
      .max(1)
      .default(0.5)
      .describe(
        "Minimum confidence score for a match to be surfaced. Below this the socket uses the not-taken sentinel.",
      ),
    entityOutputs: z
      .array(entityOutputSchema)
      .min(1)
      .superRefine((outputs, ctx) => {
        const seenIds = new Set<string>();
        outputs.forEach((output, index) => {
          if (seenIds.has(output.socketId)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: `Duplicate entityOutputs[].socketId: ${output.socketId}`,
              path: [index, "socketId"],
            });
            return;
          }
          seenIds.add(output.socketId);
        });
      })
      .describe(
        "Entity types to resolve against. Each becomes a dynamic output socket.",
      ),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "5 minutes",
    retry: {
      maximumAttempts: 2,
    },
  },
});
